import {Button, Switch} from '@wandb/weave/components';
import {RunState} from '@wandb/weave/components/RunState';
import {Tag} from '@wandb/weave/components/Tag';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useParams} from 'react-router-dom';

import {
  useActiveErrorDrawer,
  useActiveIssues,
  useActiveRun,
  useAnnotationActive,
  useGroupedIssues,
  useHoveredIssue,
  useRunGrafanaDashboard,
  useSelectedIssue,
  useSetActiveRun,
  useSetSelectedIssue,
  useToggleIssues,
} from '../../../state/issues/hooks';
import {useMutableBox} from '../../../util/hooks/useMutableBox';
import {useSingleRunModeContext} from '../../MultiRunWorkspace/SingleRunMode';
import {SlurmJobConfirmModal} from '../../Slurm/SlurmJobConfirmModal';
import {SlurmJobDrawer} from '../../Slurm/SlurmJobDrawer/SlurmJobDrawer';
import {SlurmJobSection} from '../../Slurm/SlurmJobSection';
import {useSlurmJobActions} from '../../Slurm/useSlurmJobActions';
import {AccordionSection} from '../AccordionSection/AccordionSection';
import {useFloatingWorkspaceDrawerTransition} from '../FloatingWorkspaceDrawer';
import {useWorkspaceDrawerContext} from '../WorkspaceDrawerContextProvider';
import {EmptyRunIssuesDrawer} from './EmptyRunIssuesDrawer';
import {ErrorRunIssuesDrawer} from './ErrorRunIssuesDrawer';
import {getPriorityIssues} from './getPriorityIssues';
import {GrafanaLinkBanner} from './GrafanaBanner';
import {IssuesWelcomeBanner} from './IssuesWelcomeBanner';
import {checkIfCliIsOutdated} from './outdatedCLICheck';
import {OutdatedCliDrawer} from './OutdatedCliDrawer';
import {GroupedRunIssuesList, RunIssuesList} from './RunIssue';

export const RunIssuesDrawer = () => {
  const {isSingleRun} = useSingleRunModeContext();
  const {entityName = '', projectName = ''} = useParams<{
    entityName: string;
    projectName: string;
  }>();
  const activeErrorDrawer = useActiveErrorDrawer();
  const activeIssues = useActiveIssues();
  const activeRun = useActiveRun();
  const annotationActive = useAnnotationActive();
  const hoveredIssue = useHoveredIssue();
  const groupedIssues = useGroupedIssues();
  const runGrafanaDashboard = useRunGrafanaDashboard();
  const selectedIssue = useSelectedIssue();
  const setActiveRun = useSetActiveRun();
  const setSelectedIssue = useSetSelectedIssue();
  const toggleIssues = useToggleIssues();
  const activeRunName = activeRun?.name ?? '';
  const isActiveRunLive = activeRun?.state === 'running';

  const slurm = useSlurmJobActions({
    entityName,
    projectName,
    runName: activeRunName,
    skip: !activeRun?.name,
  });

  const [confirmVariant, setConfirmVariant] = useState<
    'cancel' | 'requeue' | null
  >(null);
  const [showSlurmDrawer, setShowSlurmDrawer] = useState(false);

  const outdatedSDK = checkIfCliIsOutdated(activeRun);

  const {closeWorkspaceDrawer, contentType} = useWorkspaceDrawerContext();
  const [targetSection, setTargetSection] = useState<'overview' | 'all-issues'>(
    'overview'
  );

  const headerRef = useRef<HTMLDivElement>(null);
  const getContentType = useMutableBox(contentType);
  const getSelectedIssue = useMutableBox(selectedIssue);
  const getTargetSection = useMutableBox(targetSection);

  useEffect(() => {
    return () => {
      if (!isSingleRun) {
        setActiveRun(null);
      }
    };
  }, [isSingleRun, setActiveRun]);

  // 'final' and 'before' are set by the clickable icons in Layout on the <IssueAlert> component
  const handleScrollToIssue = useCallback(() => {
    const selectedIssue = getSelectedIssue();
    if (!selectedIssue) {
      return;
    }

    const isLiveIssue = selectedIssue !== 'final' && selectedIssue !== 'before';
    if (contentType === 'run-issues' && selectedIssue && isLiveIssue) {
      const issueToScrollTo = document.getElementById(selectedIssue);
      if (issueToScrollTo) {
        issueToScrollTo.scrollIntoView({behavior: 'smooth'});
      }
    }
  }, [contentType, getSelectedIssue]);

  useFloatingWorkspaceDrawerTransition('transitionend', handleScrollToIssue);

  useEffect(() => {
    if (contentType !== 'run-issues') {
      setSelectedIssue(null);
    } else {
      if (
        getSelectedIssue() &&
        getSelectedIssue() !== 'final' &&
        getSelectedIssue() !== 'before'
      ) {
        setTargetSection('all-issues');
      } else {
        setTargetSection('overview');
      }
    }
  }, [contentType, setSelectedIssue, getSelectedIssue]);

  useEffect(() => {
    if (
      !selectedIssue ||
      !getContentType() ||
      getContentType() !== 'run-issues' ||
      !getTargetSection()
    ) {
      return;
    }

    if (selectedIssue === 'final' || selectedIssue === 'before') {
      if (getTargetSection() !== 'overview') {
        setTargetSection('overview');
      }
      if (headerRef.current) {
        headerRef.current.scrollIntoView({behavior: 'smooth'});
      }
    } else {
      if (getTargetSection() !== 'all-issues') {
        setTargetSection('all-issues');
      }
      const issueToScrollTo = document.getElementById(selectedIssue);
      if (issueToScrollTo) {
        issueToScrollTo.scrollIntoView({behavior: 'smooth'});
      }
    }
  }, [selectedIssue, getContentType, getTargetSection]);

  const status: 'error' | 'empty' | 'issues' = useMemo(() => {
    if (activeErrorDrawer) {
      return 'error';
    }
    if (activeIssues.length === 0) {
      return 'empty';
    }
    return 'issues';
  }, [activeIssues, activeErrorDrawer]);

  const {priorityIssue, priorityIssueDescription} = useMemo(() => {
    const issue = getPriorityIssues(activeIssues, activeRun?.state ?? '');
    return {
      priorityIssue: issue,
      priorityIssueDescription:
        (issue[0]?.updatedDescription ? issue[0]?.updatedDescription : '') +
        ' ' +
        (issue[0]?.resolution ? issue[0]?.resolution : ''),
    };
  }, [activeIssues, activeRun?.state]);

  return (
    <>
      {/* In multi-run workspaces, activeRun may come from a cross-project runset
          whose entity/project differs from the route params. Until ActiveRun carries
          its own entity/project, block mutations to avoid targeting the wrong project. */}
      {slurm.showSlurmActions && isSingleRun && (
        <SlurmJobConfirmModal
          isOpen={confirmVariant != null}
          variant={confirmVariant ?? 'cancel'}
          jobId={slurm.jobId}
          entityName={entityName}
          projectName={projectName}
          runName={activeRunName}
          isRunLive={isActiveRunLive}
          onClose={() => setConfirmVariant(null)}
        />
      )}
      {slurm.showSlurmActions && (
        <SlurmJobDrawer
          open={showSlurmDrawer}
          onClose={() => setShowSlurmDrawer(false)}
          jobId={slurm.jobId}
          entityName={entityName}
          projectName={projectName}
          runName={activeRunName}
          clusterName={slurm.clusterName ?? undefined}
          region={slurm.region ?? undefined}
          isRunLive={isActiveRunLive}
          readOnly={slurm.readOnly || !isSingleRun}
          grafanaUrl={slurm.grafanaUrl || runGrafanaDashboard || undefined}
          metadata={slurm.metadata ?? undefined}
        />
      )}
      <div className="flex h-full w-full flex-col">
        <header
          className="flex-grow-0 border-wb-border px-20 py-12"
          data-test="run-issues-drawer-header"
          ref={headerRef}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-8">
              <h1 className="text-xl font-semibold leading-10">Issues</h1>
              {!activeErrorDrawer && (
                <Tag label={activeIssues.length.toString()} color="moon" />
              )}
            </div>
            <div className="flex items-center gap-8">
              {isSingleRun && (
                <>
                  <label htmlFor="annotate-issues-switch">
                    Annotate issues
                  </label>
                  <Switch.Root
                    checked={annotationActive}
                    id="annotate-issues-switch"
                    onCheckedChange={toggleIssues}
                    size="medium">
                    <Switch.Thumb checked={annotationActive} size="medium" />
                  </Switch.Root>
                </>
              )}

              <Button
                startIcon="close"
                variant="ghost"
                onClick={() => closeWorkspaceDrawer(false)}
              />
            </div>
          </div>
        </header>
        {status === 'error' && !outdatedSDK && <ErrorRunIssuesDrawer />}
        {status === 'error' && outdatedSDK && (
          <OutdatedCliDrawer
            version={
              typeof activeRun?._wandb?.cli_version === 'string'
                ? activeRun?._wandb?.cli_version
                : ''
            }
          />
        )}
        {status !== 'error' && (
          <>
            <AccordionSection
              initialOpen={targetSection === 'overview'}
              title="Overview">
              <IssuesWelcomeBanner />

              {/* Note: The Grafana banner is hidden when the Slurm feature is enabled (replaced by
                the "View in Grafana" button in the Slurm section). For non-Slurm users, we render
                the banner even without a link to avoid a display race condition. */}
              {!slurm.showSlurmActions && (
                <GrafanaLinkBanner link={runGrafanaDashboard ?? ''} />
              )}

              <div className="mt-16 flex h-24 items-center justify-start gap-8">
                <div className="text-base font-semibold">Run</div>

                <RunState value={activeRun?.state ?? ''} />
              </div>
              <div className="mt-4">{activeRun?.displayName}</div>

              {slurm.showSlurmActions && (
                <SlurmJobSection
                  jobId={slurm.jobId}
                  isRunLive={isActiveRunLive}
                  readOnly={slurm.readOnly || !isSingleRun}
                  grafanaUrl={runGrafanaDashboard ?? undefined}
                  onViewDetails={() => {
                    slurm.refetch();
                    setShowSlurmDrawer(true);
                  }}
                  onCancel={() => setConfirmVariant('cancel')}
                  onRequeue={() => setConfirmVariant('requeue')}
                />
              )}

              <div className="mb-4 mt-16 text-base font-semibold">
                Issue summary
              </div>
              <p>
                CoreWeave continuously monitors infrastructure to ensure stable
                and reliable operations. Any critical issues will appear here.
              </p>

              <RunIssuesList
                hoveredIssue={hoveredIssue}
                issues={priorityIssue}
              />
              {priorityIssueDescription && (
                <div className="mt-16">{priorityIssueDescription}</div>
              )}
            </AccordionSection>
            <AccordionSection
              initialOpen={targetSection === 'all-issues'}
              title="All issues">
              {status === 'empty' && <EmptyRunIssuesDrawer />}

              {status === 'issues' && (
                <div className="mt-16" data-test="run-issues-drawer-issues">
                  <GroupedRunIssuesList
                    hoveredIssue={hoveredIssue}
                    groupedIssues={groupedIssues}
                  />
                </div>
              )}
            </AccordionSection>
          </>
        )}
      </div>
    </>
  );
};
